import { THINKING_WARN_THRESHOLD_MS } from '@orn/global/assistant/timing';
import {
  JSON_PUNCTUATION_PADDING_PATTERN,
  OPAQUE_TRANSCRIPT_VALUE_PATTERN,
} from '@orn/global/assistant/transcriptSearch';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import { CopyIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import formatInstantInTimeZone, { PACIFIC_TIME_ZONE } from '@orn/shared-web/utils/datetime/formatInstantInTimeZone';
import formatTimeAgo from '@orn/shared-web/utils/datetime/formatTimeAgo';
import { centeredScrollTop } from '@orn/shared-web/utils/dom/centeredScrollTop';
import { countMatches } from '@orn/shared-web/utils/splitOnMatches';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import HighlightedText from '../../../components/HighlightedText';
import IconButton from '../../../components/IconButton';
import InspectLinkedText from '../../../components/InspectLinkedText';
import JsonView from '../../../components/JsonView';
import { useToast } from '../../../components/Toast';
import { assistantFeedbackAnchorPath } from '../../../paths';
import { formatTokens } from '../promptBudgetView';

import AttachmentDownloadLink from './AttachmentDownloadLink';
import MetricChip from './MetricChip';
import {
  AUDIT_PARAM_COLOR_CLASSES,
  AUDIT_PARAM_KEYS,
  buildToolCallArgsIndex,
  buildTurnClipboardText,
  buildTurnSegments,
  COLLAPSED_TOOL_RESULT_KEYS,
  DEFAULT_ROLE_ACCENT,
  ELLIPSIZED_STRING_KEYS,
  extractTextBlocks,
  formatCostUsd,
  formatDurationMs,
  formatTurnDelta,
  groupHasNudgedUnsentAnswer,
  groupHasSlowThinkingRound,
  groupNudgeWentUnanswered,
  isSlowThinkingRound,
  PROMINENT_CONTENT_COLOR_CLASSES,
  PROSE_COLOR_CLASSES,
  readCreatedAlertId,
  readObservationDocuments,
  readPayloadKind,
  readToolCallId,
  readToolResultPayload,
  ROLE_ACCENT,
  formatToolParams,
  type AlertNotifyChannel,
  type FeedbackRow,
  type IRoleAccent,
  type ITurnGroup,
  type MessageRow,
} from './transcriptModel';

/** Same excision the server's haystack applies, so both sides count the same characters. */
const OPAQUE_TRANSCRIPT_VALUE_RE = new RegExp(OPAQUE_TRANSCRIPT_VALUE_PATTERN, 'g');

/** Same collapse, applied to this side's serialization and to the query alike. */
const JSON_PUNCTUATION_PADDING_RE = new RegExp(JSON_PUNCTUATION_PADDING_PATTERN, 'g');

/**
 * Renders a message's searchable text the way the server's haystack does:
 * opaque values excised, and the padding `jsonb::text` writes after `:` and
 * `,` collapsed away. Runs over the query too — a needle left un-collapsed
 * would stop matching the haystack it was normalized against.
 */
const toSearchableText = (serialized: string): string =>
  serialized.replace(OPAQUE_TRANSCRIPT_VALUE_RE, '\n').replace(JSON_PUNCTUATION_PADDING_RE, '$1');

interface IParallelMetadataProps {
  /** ms since epoch — earliest `startedAt` across the parallel siblings. */
  startedAtMs: number | null;
  /** Max `latencyMs` across the siblings (the slowest tool gates the block). */
  maxLatencyMs: number | null;
  /** ms since epoch of the turn's earliest `startedAt`, for the Δ chip. */
  turnStartedAtMs: number | null;
}

/**
 * Wraps `MetricChip`s in a per-row metadata container matching
 * MessageCard's strip. Used by the parallel-group header to
 * align its Δ / lat chips with the per-row chips below.
 */
const ParallelMetadata = ({
  startedAtMs,
  maxLatencyMs,
  turnStartedAtMs,
}: IParallelMetadataProps): React.ReactElement => (
  <span className="text-fg-tertiary flex flex-1 justify-end gap-3 font-mono normal-case tabular-nums">
    <MetricChip label="" value="" />
    <span title="Delta from turn start: earliest startedAt across the parallel siblings (≈ when the assistant dispatched the parallel tool batch).">
      <MetricChip
        label="Δ"
        value={formatTurnDelta(startedAtMs !== null ? new Date(startedAtMs) : null, turnStartedAtMs)}
      />
    </span>
    <MetricChip label="" value="" />
    <MetricChip label="" value="" />
    <span title="Group latency: max(latencyMs) across the parallel siblings. The slowest tool gates the next round, so this is the wall-clock duration of the parallel block.">
      <MetricChip label="lat" value={formatDurationMs(maxLatencyMs)} valueClass="text-fg-primary" />
    </span>
  </span>
);

interface IToolExpansionSectionProps {
  /** Sub-section label rendered above the JSON (e.g. `toolCall`, `toolResult`). */
  label: string;
  children: React.ReactNode;
}

/**
 * A labeled sub-section inside an expanded toolResult card. Splits the
 * expansion into the originating `toolCall` and its `toolResult` so an
 * operator can read the call's arguments (with the audit params surfaced)
 * alongside the result.
 */
const ToolExpansionSection = ({ label, children }: IToolExpansionSectionProps) => (
  <section>
    <div className="text-fg-quaternary mb-1 text-[9px] uppercase tracking-wider">{label}</div>
    {children}
  </section>
);

interface ICollapsibleMessageCardProps {
  message: MessageRow;
  accent: IRoleAccent;
  metadata: React.ReactNode;
  /** Extra header bits sitting next to the role label (toolName, failed badge, kind discriminator, etc). */
  headerExtras?: React.ReactNode;
  /**
   * Optional one-line preview of the message body, rendered inline
   * after the role label and ellipsized to fit the header row. Lets an
   * operator scan a transcript without expanding every card. Full text
   * is still in the JSON below on expand. Accepts `ReactNode` so
   * callers can render styled placeholders (e.g. italic `Thinking…` on
   * a tool-use-only assistant round).
   */
  headerPreview?: React.ReactNode;
  /**
   * Custom expanded body, rendered in place of the default whole-payload
   * `JsonView`. Lets a role split the expansion into labeled sub-sections
   * (e.g. a toolResult's `toolCall` + `toolResult`). Only constructed by
   * the caller, but only mounted when the card is open.
   */
  expandedBody?: React.ReactNode;
  /**
   * Per-key value colors forwarded to the default-body `JsonView` (e.g. a
   * user message's `content` bold yellow). When omitted, the default body
   * falls back to the bold bright-white prose treatment. Ignored when
   * `expandedBody` is set.
   */
  stringKeyClasses?: ReadonlyMap<string, string>;
  /**
   * Content rendered below the header regardless of collapse state — i.e.
   * outside the collapse contract the rest of the card follows. Use for
   * always-actionable affordances that shouldn't require expanding the card
   * (e.g. attachment download links on an `attachment` observation). Sits
   * between the header button and the `errorMessage` line.
   */
  alwaysVisibleBody?: React.ReactNode;
  /** Active transcript-search term, forwarded to the expanded JSON body. */
  highlightQuery?: string | null;
}

/**
 * Shared collapsible card. Five roles use it (toolResult, assistant,
 * user, system_instruction, system_observation) — the JSON payload is
 * hidden by default and expands on click. Header shows the chevron,
 * role label, optional extras, an optional ellipsized preview of the
 * message body, and the right-aligned metadata strip.
 */
const CollapsibleMessageCard = ({
  message,
  accent,
  metadata,
  headerExtras,
  headerPreview,
  expandedBody,
  stringKeyClasses,
  alwaysVisibleBody,
  highlightQuery,
}: ICollapsibleMessageCardProps) => {
  const [expanded, setExpanded] = React.useState(false);
  const toggle = () => setExpanded((v) => !v);
  // How many times the search term appears anywhere in this message. The badge
  // earns its place by finding hits the collapsed header cannot show, which is
  // most of them: a match in a tool's arguments or in the model's thinking is
  // invisible until the card is opened, and without a count the operator has no
  // way to know which card is worth opening.
  //
  // Counted over the same fields the server counts and through the same
  // normalization, so the per-message badges sum to the thread's badge in the
  // rail rather than approximately agreeing with it.
  const hitCount = React.useMemo(() => {
    if (highlightQuery === null || highlightQuery === undefined || highlightQuery.length === 0) {
      return 0;
    }
    const needle = toSearchableText(highlightQuery);
    return (
      countMatches(toSearchableText(JSON.stringify(message.payload) ?? ''), needle) +
      countMatches(message.errorMessage ?? '', needle) +
      countMatches(message.modelId ?? '', needle)
    );
  }, [message.payload, message.errorMessage, message.modelId, highlightQuery]);
  return (
    <article className={cx('even:bg-bg-secondary/40 my-1 border-l-2 py-0.5 pl-3 pr-2', accent.border)}>
      <button
        type="button"
        onClick={toggle}
        className="hover:bg-bg-secondary -ml-1 flex w-full items-center gap-2 px-1 py-0.5 text-left font-mono text-[10px] uppercase"
        aria-expanded={expanded}
      >
        <span className="text-fg-quaternary shrink-0">{expanded ? '▾' : '▸'}</span>
        <span className={cx('shrink-0', accent.label)}>{message.role}</span>
        {headerExtras !== undefined && <span className="shrink-0">{headerExtras}</span>}
        {headerPreview !== undefined && (
          <span className={cx('min-w-0 flex-1 truncate normal-case', accent.prose)}>{headerPreview}</span>
        )}
        {hitCount > 0 && (
          <span
            className="border-border-brand text-fg-brand-primary shrink-0 border px-1 py-px text-[9px] normal-case"
            title={expanded ? undefined : 'Expand the card to see the highlighted matches'}
          >
            {hitCount} hit{hitCount === 1 ? '' : 's'}
          </span>
        )}
        <span className="text-fg-tertiary ml-auto flex shrink-0 gap-2">{metadata}</span>
      </button>
      {expanded && (
        <div className="whitespace-pre-wrap pl-1 pt-2 font-mono text-[11px] leading-snug">
          {expandedBody ?? (
            <JsonView
              value={message.payload}
              collapseArrayKeys={message.role === 'toolResult' ? COLLAPSED_TOOL_RESULT_KEYS : undefined}
              stringKeyClasses={stringKeyClasses ?? PROMINENT_CONTENT_COLOR_CLASSES}
              ellipsizeStringKeys={ELLIPSIZED_STRING_KEYS}
              highlightQuery={highlightQuery}
              linkUuids
            />
          )}
          {/* `modelId` is a column, not part of the payload, so the JSON above
              never shows it. Rendered here because transcript search matches
              against it: without this, a model-id query returns a card badged
              with a hit count and nothing on screen that matches. Expanded-only
              — it is the same value on every assistant row, so it would be
              noise in the collapsed strip. */}
          {message.modelId !== null && message.modelId !== undefined && (
            <div className="text-fg-tertiary mt-1">
              model: <HighlightedText text={message.modelId} query={highlightQuery} />
            </div>
          )}
        </div>
      )}
      {alwaysVisibleBody !== undefined && <div className="pl-1 pt-1.5">{alwaysVisibleBody}</div>}
      {message.errorMessage && (
        <div className="text-fg-error-primary mt-1 font-mono text-[11px]">
          errorMessage: <InspectLinkedText text={message.errorMessage} query={highlightQuery} />
        </div>
      )}
    </article>
  );
};

const NO_NOTIFY_CHANNELS: readonly AlertNotifyChannel[] = [];

/**
 * Chip tooltip for a badged `alert_create`. States the claim's exact scope:
 * dispatch-level truth (the notify tool's own contract — hand-off made, not
 * delivery confirmed), correlated across this thread's loaded rows only. An
 * *unbadged* create can still have been notified — from another thread, a
 * not-yet-loaded page, or any conversational thread (those never carry
 * `alert_notify` at all).
 */
const notifiedChipTitle = (channel: AlertNotifyChannel): string => {
  const base =
    channel === 'unknown'
      ? 'Covered by a dispatched alert_notify in this thread; the originating call (and its channel) is not among the loaded rows.'
      : `Covered by a dispatched alert_notify in this thread, on ${channel}.`;
  return `${base} Dispatch means the hand-off was made, not that delivery is confirmed. Only this thread's loaded rows are correlated — an unbadged create may still have been notified elsewhere.`;
};

interface IMessageCardProps {
  message: MessageRow;
  /** ms since epoch of the turn's earliest `startedAt`, for the Δ chip. */
  turnStartedAtMs: number | null;
  /**
   * Map of `toolUseId → input` built once per turn. The toolResult
   * branch reads this to render a one-line param summary next to the
   * tool name. Non-toolResult rows ignore it.
   */
  toolCallArgs: Map<string, unknown>;
  /** Active transcript-search term; every occurrence rendered in this card is highlighted. */
  highlightQuery?: string | null;
  /**
   * alertId → channels a dispatched `alert_notify` covered it on, built once
   * over every loaded message (notifies can land turns after their creates).
   * The toolResult branch badges matching `alert_create` cards; other rows
   * ignore it.
   */
  alertNotifications: ReadonlyMap<string, readonly AlertNotifyChannel[]>;
}

const MessageCard = ({
  message,
  turnStartedAtMs,
  toolCallArgs,
  highlightQuery,
  alertNotifications,
}: IMessageCardProps) => {
  const accent = ROLE_ACCENT[message.role] ?? DEFAULT_ROLE_ACCENT;
  // Each cell is two stacked fixed-width sub-cells: label
  // (right-aligned, `w-[3ch]`) and value (right-aligned, `w-[7ch]`)
  // with a tight gap between. Labels land at the same screen-X across
  // every row (right edge of the label sub-cell), values' right edges
  // align too, and the inter-pair gap is fixed regardless of value
  // length. Empty cells still hold their width so alignment doesn't
  // collapse when a chip is absent. `normal-case` breaks out of the
  // parent header's `uppercase` so digits stay readable. `modelId` is
  // intentionally dropped from per-row chips — it's the same
  // hardcoded value on every assistant row in a thread; the JSON
  // expansion still has it.
  const hasLatency = message.latencyMs !== null && message.latencyMs !== undefined;
  const hasThinking = message.thinkingMs !== null && message.thinkingMs !== undefined;
  const slowThinking = isSlowThinkingRound(message);
  const hasTtft = message.ttftMs !== null && message.ttftMs !== undefined;
  const metadata = (
    <span className="flex items-center gap-3 font-mono normal-case tabular-nums">
      <span title="Per-thread sequence: monotonic counter assigned at append time. Stable across rehydration; used as the keyset cursor for incremental loads.">
        <MetricChip label="seq" value={String(message.sequence)} />
      </span>
      <span title="Delta from turn start: orchestrator wall-clock between this row's startedAt and the first row of the turn. The first row of each turn renders +0ms.">
        <MetricChip label="Δ" value={formatTurnDelta(message.startedAt ?? null, turnStartedAtMs)} />
      </span>
      <span
        title={
          slowThinking
            ? `Wall-clock time the model spent inside thinking blocks during this round. ⚠️ Exceeds the ${THINKING_WARN_THRESHOLD_MS / 1000}s slow-thinking threshold — this round also fires a Sentry warning.`
            : 'Wall-clock time the model spent inside thinking blocks during this round. Summed across interleaved blocks (Anthropic extended thinking). NULL on tool-result, synthetic, and non-thinking rows.'
        }
      >
        <MetricChip
          label={hasThinking ? '🧠' : ''}
          value={hasThinking ? formatDurationMs(message.thinkingMs ?? null) : ''}
          valueClass={slowThinking ? 'text-fg-warning-primary font-bold' : ''}
        />
      </span>
      <span title="Time-to-first-token: orchestrator wall-clock from round start to the first user-facing text token. Excludes thinking and tool-call time — the user-perceived latency.">
        <MetricChip label={hasTtft ? 'ttft' : ''} value={hasTtft ? formatDurationMs(message.ttftMs ?? null) : ''} />
      </span>
      <span title="Latency: full wall-clock duration of this round (completedAt − startedAt). For tool-result rows, the tool execution time. Includes thinking + post-text streaming for assistant rounds.">
        <MetricChip
          label={hasLatency ? 'lat' : ''}
          value={hasLatency ? formatDurationMs(message.latencyMs ?? null) : ''}
          valueClass="text-fg-primary"
        />
      </span>
    </span>
  );

  if (message.role === 'toolResult') {
    const toolMeta = readToolResultPayload(message.payload);
    const toolCallId = readToolCallId(message.payload);
    const toolCallArguments = toolCallId !== null ? toolCallArgs.get(toolCallId) : undefined;
    const hasToolCall = toolCallArguments !== undefined;
    const paramsPreview = formatToolParams(toolCallArguments, AUDIT_PARAM_KEYS);
    const createdAlertId = readCreatedAlertId(message.payload);
    const notifiedChannels =
      createdAlertId !== null ? (alertNotifications.get(createdAlertId) ?? NO_NOTIFY_CHANNELS) : NO_NOTIFY_CHANNELS;
    return (
      <CollapsibleMessageCard
        message={message}
        accent={accent}
        metadata={metadata}
        highlightQuery={highlightQuery}
        headerPreview={
          paramsPreview.length > 0 ? <HighlightedText text={paramsPreview} query={highlightQuery} /> : undefined
        }
        headerExtras={
          <span className="inline-flex items-center gap-1.5">
            {toolMeta.toolName && (
              <span className="font-bold normal-case text-fuchsia-400">
                <HighlightedText text={toolMeta.toolName} query={highlightQuery} />
              </span>
            )}
            {toolMeta.isError && (
              <span className="text-fg-error-primary border-fg-error-primary inline-flex items-center border px-1 py-px text-[9px] uppercase tracking-wider">
                failed
              </span>
            )}
            {notifiedChannels.map((channel) => (
              <span
                key={channel}
                title={notifiedChipTitle(channel)}
                className="text-fg-success-primary border-fg-success-primary inline-flex items-center border px-1 py-px text-[9px] uppercase tracking-wider"
              >
                📨 notified{channel !== 'unknown' && ` · ${channel}`}
              </span>
            ))}
          </span>
        }
        expandedBody={
          hasToolCall ? (
            <div className="flex flex-col gap-3">
              <ToolExpansionSection label="toolCall">
                <JsonView
                  value={toolCallArguments}
                  stringKeyClasses={AUDIT_PARAM_COLOR_CLASSES}
                  highlightQuery={highlightQuery}
                  linkUuids
                />
              </ToolExpansionSection>
              <ToolExpansionSection label="toolResult">
                <JsonView
                  value={message.payload}
                  collapseArrayKeys={COLLAPSED_TOOL_RESULT_KEYS}
                  stringKeyClasses={PROMINENT_CONTENT_COLOR_CLASSES}
                  highlightQuery={highlightQuery}
                  linkUuids
                />
              </ToolExpansionSection>
            </div>
          ) : undefined
        }
      />
    );
  }

  if (message.role === 'assistant' || message.role === 'user') {
    const text = extractTextBlocks(message.payload).join(' ');
    // Tool-use-only assistant rounds emit no user-facing text — the
    // model decided to call tools first. Surface a dimmed italic
    // placeholder so the row reads as "no message yet, still working"
    // rather than as an empty line the operator might miss.
    const preview: React.ReactNode =
      text.length > 0 ? (
        <HighlightedText text={text} query={highlightQuery} />
      ) : message.role === 'assistant' ? (
        <span className="text-fg-tertiary italic">Thinking…</span>
      ) : undefined;
    return (
      <CollapsibleMessageCard
        message={message}
        accent={accent}
        metadata={metadata}
        headerPreview={preview}
        stringKeyClasses={PROSE_COLOR_CLASSES}
        highlightQuery={highlightQuery}
      />
    );
  }

  if (message.role === 'system_instruction' || message.role === 'system_observation') {
    const kind = readPayloadKind(message.payload);
    // Attachment observations carry the uploaded documents; surface each as
    // an always-visible download link so an operator can open the file
    // without expanding the JSON.
    const documents =
      message.role === 'system_observation' && kind === 'attachment' ? readObservationDocuments(message.payload) : [];
    return (
      <CollapsibleMessageCard
        message={message}
        accent={accent}
        metadata={metadata}
        highlightQuery={highlightQuery}
        headerExtras={
          kind && (
            <span className="text-fg-secondary normal-case">
              <HighlightedText text={kind} query={highlightQuery} />
            </span>
          )
        }
        alwaysVisibleBody={
          documents.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {documents.map((doc) => (
                <AttachmentDownloadLink key={doc.id} documentId={doc.id} name={doc.name} />
              ))}
            </div>
          ) : undefined
        }
      />
    );
  }

  // Fallthrough — unknown roles render uncollapsed (their shape is
  // unknown, so the JSON IS the signal). Add a dedicated branch above
  // when a new role needs custom rendering.
  return (
    <article className={cx('even:bg-bg-secondary/40 my-1 border-l-2 py-0.5 pl-3 pr-2', accent.border)}>
      <header className="text-fg-tertiary flex gap-2 font-mono text-[10px] uppercase">
        <span className={accent.label}>{message.role}</span>
        {metadata}
      </header>
      <div className="whitespace-pre-wrap pl-1 pt-1 font-mono text-[11px] leading-snug">
        <JsonView
          value={message.payload}
          stringKeyClasses={PROMINENT_CONTENT_COLOR_CLASSES}
          ellipsizeStringKeys={ELLIPSIZED_STRING_KEYS}
          highlightQuery={highlightQuery}
          linkUuids
        />
        {/* Same reason as the collapsible card: `modelId` is a column the
            payload JSON never carries, and search matches against it. */}
        {message.modelId !== null && message.modelId !== undefined && (
          <div className="text-fg-tertiary mt-1">
            model: <HighlightedText text={message.modelId} query={highlightQuery} />
          </div>
        )}
      </div>
      {message.errorMessage && (
        <div className="text-fg-error-primary mt-1 font-mono text-[11px]">
          errorMessage: <InspectLinkedText text={message.errorMessage} query={highlightQuery} />
        </div>
      )}
    </article>
  );
};

/**
 * Divider rendered between an interrupted turn and the connection-loss
 * continuation that follows it — marking where the WebSocket dropped mid-turn
 * and the server auto-resumed on the next connect. A transcript-reading aid
 * only; the underlying signal is the resume turn's `resume_interrupted`
 * `system_instruction` row (see `isResumeInterruptedTurn`).
 */
export const ResumeInterstitial = (): React.ReactElement => (
  // Raw `text-yellow-300` (fixed #fde272), NOT `utility-yellow-300`: the
  // `utility-*` ramp flips for light/dark, and MOPS forces dark, so
  // `utility-yellow-300` resolves to the dark olive `yellow-700`. Matches the
  // sibling `PROSE_COLOR_CLASSES` transcript yellow.
  <div className="flex items-center gap-2 px-2 py-1 font-mono text-[10px] uppercase tracking-wide text-yellow-300">
    <span className="border-border-primary flex-1 border-t" />
    <span className="shrink-0">↯ connection lost — resumed</span>
    <span className="border-border-primary flex-1 border-t" />
  </div>
);

/** Nearest ancestor that actually scrolls, or `null` if the page itself is the scroller. */
const findScrollingAncestor = (from: HTMLElement): HTMLElement | null => {
  for (let node = from.parentElement; node !== null; node = node.parentElement) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
};

/**
 * Brings `section` into view by scrolling only the transcript pane it lives in.
 *
 * `scrollIntoView` would scroll every scrollable ancestor including the
 * document, so jumping between search results yanked the page up and down under
 * a rail that had not moved. Falls back to `scrollIntoView` when no scrolling
 * ancestor exists — then the page IS the pane and there is nothing to protect.
 */
const scrollIntoViewWithinPane = (section: HTMLElement): void => {
  const pane = findScrollingAncestor(section);
  if (pane === null) {
    section.scrollIntoView({ block: 'center' });
    return;
  }
  const sectionRect = section.getBoundingClientRect();
  const paneRect = pane.getBoundingClientRect();
  pane.scrollTop = centeredScrollTop({
    scrollTop: pane.scrollTop,
    scrollHeight: pane.scrollHeight,
    containerHeight: pane.clientHeight,
    containerTop: paneRect.top,
    elementTop: sectionRect.top,
    elementHeight: sectionRect.height,
  });
};

interface ITurnGroupProps {
  group: ITurnGroup;
  /** Internal `/issue` feedback targeting this turn (empty when none). */
  feedback: FeedbackRow[];
  /** When true (deep-linked turn), scroll into view + ring the section. */
  highlight: boolean;
  /** Active transcript-search term; occurrences are marked throughout the turn's rows. */
  highlightQuery?: string | null;
  /**
   * alertId → notify channels, built by the caller over the *whole* loaded
   * transcript rather than per turn — an alert created in one turn can be
   * covered by a notify in a later one.
   */
  alertNotifications: ReadonlyMap<string, readonly AlertNotifyChannel[]>;
}

/**
 * Render one turn group: a sticky header summarizing the turn (start
 * timestamp, turnId, message count, round count, tool-call count,
 * duration) followed by the group's rows indented under a left border
 * so the boundary between turns is visible. `MessageCard` receives the
 * group's `startedAtMs` so it can render a Δ-from-turn-start chip.
 *
 * Start-timestamp source: the group's `startedAtMs` (orchestrator-
 * observed turn start). Falls back to the first row's `createdAt` for
 * legacy rows that predate the timing columns — keeps the chip
 * meaningful across the data migration boundary.
 *
 * Parallel tool calls are bracketed: contiguous `toolResult` runs of
 * 2+ get a `⇄ N parallel` header so an operator can tell at a glance
 * that those rows dispatched concurrently rather than serially (the
 * Δ-from-turn-start chips also reveal this on close inspection —
 * parallel siblings share a Δ — but the explicit bracket makes it
 * scannable).
 */
export const TurnGroup = ({ group, feedback, highlight, highlightQuery, alertNotifications }: ITurnGroupProps) => {
  const headerStartedAtMs = group.startedAtMs ?? new Date(group.messages[0].createdAt).getTime();
  const headerTimestamp = formatInstantInTimeZone(new Date(headerStartedAtMs), PACIFIC_TIME_ZONE);
  const segments = buildTurnSegments(group.messages);
  const toolCallArgs = React.useMemo(() => buildToolCallArgsIndex(group.messages), [group.messages]);
  const navigate = useNavigate();
  const { showToast } = useToast();
  const sectionRef = React.useRef<HTMLElement>(null);
  const handleCopyTranscript = async () => {
    try {
      await navigator.clipboard.writeText(buildTurnClipboardText(group));
      showToast({ type: 'success', message: 'Turn transcript copied' });
    } catch {
      showToast({ type: 'error', message: 'Failed to copy transcript' });
    }
  };
  // Deep-linked or search-matched turn: scroll into view and flash the header.
  // Start yellow on first paint (no fade-in), then drop the flag so
  // `transition-colors` fades it back to the normal header bg — a momentary
  // flash rather than a persistent ring (which read as janky against the sticky
  // header).
  const [flashing, setFlashing] = React.useState(highlight);
  React.useEffect(() => {
    const section = sectionRef.current;
    if (!highlight || section === null) {
      return;
    }
    scrollIntoViewWithinPane(section);
    const timer = setTimeout(() => setFlashing(false), 700);
    return () => clearTimeout(timer);
  }, [highlight]);
  return (
    <section ref={sectionRef} className="border-border-primary border-t first:border-t-0">
      <header
        className={cx(
          'text-fg-tertiary sticky top-0 z-10 flex flex-wrap items-center gap-3 px-2 py-1 font-mono text-[11px] uppercase tracking-wide transition-colors duration-700',
          flashing ? 'bg-bg-warning-solid' : 'bg-bg-secondary'
        )}
      >
        <span className="text-fg-primary">{headerTimestamp}</span>
        <span>turn {group.turnId.slice(0, 8)}</span>
        <span>{group.messages.length} msg</span>
        <span>{group.roundCount} rounds</span>
        <span>{group.toolCallCount} tool calls</span>
        <span className="text-fg-primary normal-case">{formatDurationMs(group.durationMs)}</span>
        {group.usage !== null && (
          <>
            <span
              className="normal-case"
              title="Tokens this turn billed, summed across its rounds. The whole prompt is re-sent every round, so this runs well above the conversation's own size."
            >
              {formatTokens(group.usage.totalTokens)} tok
            </span>
            <span
              className="text-fg-primary normal-case"
              title="What this turn cost, summed from the per-round prices the provider set at call time. Cache reads bill well below uncached input, which is why a large turn can still be cheap."
            >
              {formatCostUsd(group.usage.costUsd)}
            </span>
          </>
        )}
        {groupHasSlowThinkingRound(group) && (
          <span
            title={`A round in this turn spent over ${THINKING_WARN_THRESHOLD_MS / 1000}s inside thinking blocks — the same bar that fires the Sentry warning. The 🧠 chip on the offending round is highlighted below.`}
            className="border-fg-warning-primary text-fg-warning-primary border px-1 py-px font-mono text-[11px] normal-case"
          >
            🐌 slow thinking
          </span>
        )}
        {groupHasNudgedUnsentAnswer(group) &&
          (groupNudgeWentUnanswered(group) ? (
            <span
              title="A round in this turn tried to end without sending an answer, was nudged, and still sent nothing — the household got silence. The nudge row is below, recorded as a system_instruction rather than the instruction the model was given."
              className="border-fg-error-primary text-fg-error-primary border px-1 py-px font-mono text-[11px] normal-case"
            >
              📣 nudge unanswered
            </span>
          ) : (
            <span
              title="A round in this turn tried to end without sending an answer and was nudged; a later round then sent one, so the household was reached. The nudge row is below, recorded as a system_instruction rather than the instruction the model was given."
              className="border-fg-secondary text-fg-secondary border px-1 py-px font-mono text-[11px] normal-case"
            >
              📣 nudged
            </span>
          ))}
        {feedback.length > 0 && (
          <Tooltip
            wide
            title="Feedback"
            description={
              <div className="flex flex-col gap-2 text-left">
                {feedback.map((fb) => (
                  <div key={fb.id} className="flex flex-col gap-0.5">
                    <div className="font-mono text-[10px] uppercase tracking-wide text-white/80">
                      {fb.submitterEmail ?? fb.userId} · {formatTimeAgo(fb.createdAt)}
                    </div>
                    <div className="whitespace-pre-wrap break-words font-mono text-[11px] leading-snug">{fb.body}</div>
                  </div>
                ))}
              </div>
            }
          >
            <TooltipTrigger
              onPress={() => void navigate(assistantFeedbackAnchorPath(feedback[0].id))}
              className="border-border-brand text-fg-brand-primary hover:bg-bg-primary cursor-pointer border px-1 py-px font-mono text-[11px] normal-case"
            >
              💬 {feedback.length} feedback
            </TooltipTrigger>
          </Tooltip>
        )}
        <IconButton label="Copy turn transcript" className="ml-auto" onClick={() => void handleCopyTranscript()}>
          <CopyIcon className="size-4" />
        </IconButton>
      </header>
      <div className="border-border-primary ml-1 border-l pl-2">
        {segments.map((segment, idx) => {
          if (segment.kind === 'single') {
            return (
              <MessageCard
                key={segment.message.id}
                message={segment.message}
                turnStartedAtMs={group.startedAtMs}
                toolCallArgs={toolCallArgs}
                highlightQuery={highlightQuery}
                alertNotifications={alertNotifications}
              />
            );
          }
          return (
            <div key={`parallel-${idx}`} className="border-fg-warning-primary/30 my-1 border-l-2 pl-2">
              {/*
                Right-padding math: per-row chips end at
                `outer-right - 1rem` because each MessageCard's
                article has `pr-2` (0.5rem) AND its inner button has
                `w-full -ml-1 px-1` which adds another 0.5rem of
                effective right inset (button-right = article-content-
                right - 0.25, button-content-right = -0.25 more). The
                parallel header has no article/button wrapper, so it
                must explicitly carry the full 1rem (`pr-4`) on its
                own to land chips in the same screen column as the
                rows below.
              */}
              <div className="flex items-center gap-2 px-1 py-0.5 pr-4 font-mono text-[10px] uppercase tracking-wide">
                <span className="text-fg-quaternary">⇄</span>
                <span className="text-fg-quaternary">{segment.messages.length} parallel</span>
                <ParallelMetadata
                  startedAtMs={segment.startedAtMs}
                  maxLatencyMs={segment.maxLatencyMs}
                  turnStartedAtMs={group.startedAtMs}
                />
              </div>
              {segment.messages.map((message) => (
                <MessageCard
                  key={message.id}
                  message={message}
                  turnStartedAtMs={group.startedAtMs}
                  toolCallArgs={toolCallArgs}
                  highlightQuery={highlightQuery}
                  alertNotifications={alertNotifications}
                />
              ))}
            </div>
          );
        })}
      </div>
    </section>
  );
};
